'use client';

import styled from 'styled-components';
import { Switch, Text } from '@lmn/design-system';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { usePriceModePreference } from '@/app/common/priceModeProvider.tsx';
import usePriceMode from '@/app/components/search/hooks/usePriceMode.ts';

const Container = styled.div`
  display: flex;
  align-items: center;
  ${({ theme }) => theme.kallisto.mixins.font('BodySmall')};

  span {
    margin-right: 8px;
  }
`;

const BannerContainer = styled.div<{ variant: 'banner' }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 11px 15px;
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: 8px;
  margin: 0 16px 16px;
  background: white;
`;

const RowContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
`;

export type TotalPriceToggleVariant = 'filter' | 'banner' | 'row';

const ROW_LABEL_KEY = 'vacation.select.cc.trip_overview.show_total_price';
const LABEL_KEY = 'vacation.oss.filters.price_toggler.label';

export default function TotalPriceToggle({
  variant = 'filter',
  className,
  disabled = false,
  checked,
  onChange,
}: {
  variant?: TotalPriceToggleVariant;
  className?: string;
  disabled?: boolean;
  // The trip-overview surfaces drive the switch themselves — the apex calendar keeps working on local
  // state with the FT off — so they pass checked/onChange instead of using the stored preference.
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}) {
  const { t } = useTranslation();
  const { priceTogglerEnabled, showTotalPrice, isSingleTraveller } = usePriceMode();
  const { setShowTotalPrice } = usePriceModePreference();

  const isControlled = onChange !== undefined;
  if (!priceTogglerEnabled && !isControlled) return null;

  const label = t(variant === 'row' ? ROW_LABEL_KEY : LABEL_KEY);
  const props = {
    label,
    checked: isControlled ? !!checked : showTotalPrice,
    onChange: onChange ?? setShowTotalPrice,
    disabled: disabled || isSingleTraveller,
    ltr: true,
  };

  if (variant === 'banner') {
    const tooltip = t('vacation.oss.filters.price_toggler.tooltip');
    return (
      <BannerContainer data-testid="total-price-toggle" className={className} variant={variant}>
        <BannerText>
          <Text variant="BodySmall" weight="bold">
            {label}
          </Text>
          <Text variant="Caption" color="textSecondary" data-testid="total-price-toggle-tooltip">
            {tooltip}
          </Text>
        </BannerText>
        {/* Switch takes no aria-describedby, so the explanation goes into the accessible name. */}
        <Switch {...props} label={`${label}. ${tooltip}`} isLabelVisible={false} />
      </BannerContainer>
    );
  }

  if (variant === 'row') {
    return (
      <RowContainer data-testid="total-price-toggle" className={className}>
        <Text variant="BodySmall">{label}</Text>
        <Switch {...props} isLabelVisible={false} />
      </RowContainer>
    );
  }

  return (
    <Container data-testid="total-price-toggle" className={className}>
      <Switch {...props} />
    </Container>
  );
}
